import React, { useState, useEffect, useCallback } from 'react';
import { adminFetch } from '../../../utils/fetchInterceptor';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';

interface StarterPack {
  _id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  image: string;
  components: string[];
  targetAge: string;
  difficulty: string;
  isActive: boolean;
  stock: number;
  createdAt: string;
  updatedAt: string;
}

const StarterPacks: React.FC = () => {
  const [starterPacks, setStarterPacks] = useState<StarterPack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const difficulties = ['All', 'Beginner', 'Intermediate', 'Advanced'];

  // Fetch starter packs from API
  const fetchStarterPacks = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/starter-packs`
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch starter packs");

      const data = await response.json();
      setStarterPacks(data.starterPacks || data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      console.error("Error fetching starter packs:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStarterPacks();
  }, [fetchStarterPacks]);

  const filteredPacks = starterPacks.filter(pack => {
    const matchesSearch = pack.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pack.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDifficulty = filterDifficulty === 'All' || pack.difficulty === filterDifficulty;
    
    return matchesSearch && matchesDifficulty;
  });

  const togglePackStatus = async (id: string) => {
    try {
      setIsUpdating(true);
      const pack = starterPacks.find(p => p._id === id);
      if (!pack) return;

      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/starter-packs/${id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ isActive: !pack.isActive }),
        }
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to update starter pack status");

      await fetchStarterPacks(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update starter pack status");
      console.error("Error updating starter pack status:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const deletePack = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this starter pack?')) return;

    try {
      setIsDeleting(true);
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/starter-packs/${id}`,
        {
          method: 'DELETE',
        }
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to delete starter pack");

      await fetchStarterPacks(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete starter pack");
      console.error("Error deleting starter pack:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const updateStock = async (id: string, newStock: number) => {
    try {
      const response = await adminFetch(
        `${process.env.REACT_APP_API_URL}/highcode/starter-packs/${id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ stock: newStock }),
        }
      );

      if (!response) return;
      if (!response.ok) throw new Error("Failed to update stock");

      await fetchStarterPacks(); // Refresh the list
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update stock");
      console.error("Error updating stock:", err);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <i className="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Starter Packs</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchStarterPacks}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Starter Packs Management</h1>
          <p className="text-gray-600">Manage Arduino, Raspberry Pi, and ESP32 starter packs</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <i className="fas fa-plus mr-2"></i>
          Add New Pack
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search starter packs..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
            <select
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {difficulties.map(difficulty => (
                <option key={difficulty} value={difficulty}>{difficulty}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilterDifficulty('All');
              }}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Starter Packs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPacks.map((pack) => (
          <div key={pack._id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Pack Image */}
            <div className="h-48 relative">
              <img
                src={pack.image}
                alt={pack.name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4">
                <button
                  onClick={() => togglePackStatus(pack._id)}
                  disabled={isUpdating}
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    pack.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'}`}
                >
                  {pack.isActive ? 'Active' : 'Inactive'}
                </button>
              </div>
            </div>

            {/* Pack Content */}
            <div className="p-6">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{pack.name}</h3>
                <span className="text-2xl font-bold text-blue-600">
                  {pack.currency}{pack.price.toLocaleString()}
                </span>
              </div>
              
              <p className="text-gray-600 text-sm mb-4">{pack.description}</p>
              
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Target Age:</span>
                  <span className="font-medium">{pack.targetAge}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Difficulty:</span>
                  <span className={`font-medium ${
                    pack.difficulty === 'Beginner' ? 'text-green-600' :
                    pack.difficulty === 'Intermediate' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {pack.difficulty}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Stock:</span>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => updateStock(pack._id, Math.max(0, pack.stock - 1))}
                      className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300"
                    >
                      <i className="fas fa-minus text-xs"></i>
                    </button>
                    <span className="font-medium w-8 text-center">{pack.stock}</span>
                    <button
                      onClick={() => updateStock(pack._id, pack.stock + 1)}
                      className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300"
                    >
                      <i className="fas fa-plus text-xs"></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Components List */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Components:</h4>
                <ul className="text-xs text-gray-600 space-y-1">
                  {pack.components.slice(0, 3).map((component, index) => (
                    <li key={index} className="flex items-center">
                      <i className="fas fa-check text-green-500 mr-2"></i>
                      {component}
                    </li>
                  ))}
                  {pack.components.length > 3 && (
                    <li className="text-gray-400">
                      +{pack.components.length - 3} more components
                    </li>
                  )}
                </ul>
              </div>

              {/* Actions */}
              <div className="flex space-x-2">
                <button className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700 transition-colors">
                  <i className="fas fa-edit mr-1"></i>
                  Edit
                </button>
                <button className="flex-1 bg-green-600 text-white px-3 py-2 rounded-md text-sm hover:bg-green-700 transition-colors">
                  <i className="fas fa-eye mr-1"></i>
                  View
                </button>
                <button 
                  onClick={() => deletePack(pack._id)}
                  disabled={isDeleting}
                  className={`bg-red-600 text-white px-3 py-2 rounded-md text-sm hover:bg-red-700 transition-colors ${isDeleting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-box text-2xl text-blue-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Packs</p>
              <p className="text-2xl font-semibold text-gray-900">{starterPacks.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-check-circle text-2xl text-green-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active Packs</p>
              <p className="text-2xl font-semibold text-gray-900">
                {starterPacks.filter(p => p.isActive).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-warehouse text-2xl text-orange-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Stock</p>
              <p className="text-2xl font-semibold text-gray-900">
                {starterPacks.reduce((sum, pack) => sum + pack.stock, 0)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <i className="fas fa-dollar-sign text-2xl text-purple-600"></i>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Value</p>
              <p className="text-2xl font-semibold text-gray-900">
                ₵{starterPacks.reduce((sum, pack) => sum + (pack.price * pack.stock), 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StarterPacks;
